/**
 * JWT utility functions for decoding and extracting claims
 */

import type { PrivyLevel, PrivyState } from '@gw/ui/types';

/**
 * Decode a JWT token without verification
 * Note: This is for reading claims only, not for validation
 */
export function decodeJWT(token: string): Record<string, unknown> | null {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) {
      console.error('[JWT] Invalid token format');
      return null;
    }

    // Decode the payload (second part)
    const payload = parts[1];
    const decoded = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(decoded);
  } catch (error) {
    console.error('[JWT] Failed to decode token:', error);
    return null;
  }
}

/**
 * JWT Claims structure with custom claims
 */
export interface JWTClaims {
  sub: string;
  email?: string;
  username?: string;
  // Name fields (standard JWT claims)
  given_name?: string;
  family_name?: string;
  // Custom claims
  orgId?: string;
  orgName?: string;
  roles?: string | string[];
  permissions?: string | string[];
  organizations?: string | string[];
  // GW-814: Cognito groups (standard AWS Cognito claim for group membership)
  cognitoGroups?: string[];
  // GW-2325: Profile completeness from custom:profileComplete Cognito attribute (stored as string "true"/"false")
  profileComplete?: boolean | string;
  // GW-4426: Privy fields from custom claims
  privyLevel?: PrivyLevel; // custom:privyLevel
  privyState?: PrivyState; // custom:privyState
  [key: string]: unknown;
}

/**
 * Extract custom claims from a JWT token
 */
export function extractCustomClaims(token: string): JWTClaims | null {
  const decoded = decodeJWT(token);
  if (!decoded) {
    return null;
  }

  // Extract all custom: prefixed claims
  const customClaims: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(decoded)) {
    if (key.startsWith('custom:')) {
      const cleanKey = key.replace('custom:', '');
      customClaims[cleanKey] = value;
    }
  }

  // Cognito stores all custom attributes as strings in the JWT payload.
  // Coerce known numeric fields back to numbers so strict-equality checks
  // (e.g. privyLevel === 0) work correctly at runtime.
  if (typeof customClaims['privyLevel'] === 'string') {
    const n = parseInt(customClaims['privyLevel'], 10);
    if (!isNaN(n)) customClaims['privyLevel'] = n;
  }

  // GW-814: Extract cognito:groups (standard AWS Cognito claim for group membership)
  // This is where Cognito stores user group information like 'global_admin'
  const cognitoGroups = decoded['cognito:groups'];

  return {
    sub: decoded.sub as string,
    email: decoded.email as string | undefined,
    username: decoded['cognito:username'] as string | undefined,
    // Extract standard name fields from JWT
    given_name: decoded.given_name as string | undefined,
    family_name: decoded.family_name as string | undefined,
    // GW-814: Include cognito:groups for role checking
    cognitoGroups: Array.isArray(cognitoGroups) ? (cognitoGroups as string[]) : undefined,
    ...customClaims,
  };
}

/**
 * GW-9327: Extract the active-org claim (`custom:orgId`) from a JWT.
 *
 * Backend authorization keys exclusively on the JWT active-org claim, so the
 * SPA's `currentOrg` selection must be reconciled against this value — a
 * divergence sends org-scoped requests (e.g. token balance) to an org the
 * token is not scoped to, which the backend rejects with 403.
 *
 * `extractCustomClaims` already maps `custom:orgId` → `orgId`. Returns `null`
 * for an absent claim or an absent/malformed token.
 */
export function getActiveOrgIdFromToken(token: string): string | null {
  const claims = extractCustomClaims(token);
  const orgId = claims?.orgId;
  return typeof orgId === 'string' && orgId.length > 0 ? orgId : null;
}

/**
 * Parse roles from JWT claims
 * Roles can be a JSON string array or a single string
 */
export function parseRoles(rolesValue: unknown): string[] {
  if (!rolesValue) {
    return [];
  }

  // If it's already an array
  if (Array.isArray(rolesValue)) {
    return rolesValue;
  }

  // If it's a JSON string
  if (typeof rolesValue === 'string') {
    try {
      const parsed = JSON.parse(rolesValue);
      return Array.isArray(parsed) ? parsed : [parsed];
    } catch {
      // If parsing fails, treat as single role
      return [rolesValue];
    }
  }

  return [];
}

/**
 * Check if a JWT token is expired or will expire within the buffer window
 * @param token - JWT string to check
 * @param bufferSeconds - Seconds before actual expiry to treat as expired (default: 30)
 * @returns true if token is expired or will expire within bufferSeconds
 */
export function isTokenExpired(token: string, bufferSeconds = 30): boolean {
  const decoded = decodeJWT(token);
  if (!decoded || typeof decoded.exp !== 'number') {
    // Cannot determine expiry — treat as expired to be safe
    return true;
  }
  const expiryMs = decoded.exp * 1000;
  return Date.now() >= expiryMs - bufferSeconds * 1000;
}
